import React, { FC, useState } from 'react';
import { Popover } from 'antd';
import moment from 'moment-timezone';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import { getFamilyNameAndGivenName } from '../../utils/FieldTechUtils';
import { cn } from '../../utils/ClassNamesUtils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { ServiceCallPopoverDisplayV2 } from '../service_call_popover_display/ServiceCallPopoverDisplayV2';

const TIME_FORMAT = 'h:mm A';

// This is to render just enough big height of event to render text in it! Saw this in google calendar and implemented here
const GRID_HOUR_HEIGHT_PX = 90;
const CARD_ROW_HEIGHT_PX = 20;
const CARD_VERTICAL_PADDING_PX = 4;
const CARD_ROW_GAP_PX = 4;
const TWO_ROW_MINIMUM_PX = CARD_ROW_HEIGHT_PX * 2 + CARD_ROW_GAP_PX + CARD_VERTICAL_PADDING_PX;
const THREE_ROW_MINIMUM_PX = CARD_ROW_HEIGHT_PX * 3 + CARD_ROW_GAP_PX + CARD_VERTICAL_PADDING_PX;
const TECHNICIAN_AVATAR_SIZE_PX = 20;

// The calendar's work orders come from the search endpoint, whose records are flat: they carry
// `lastServiceCallLeadTechnicianName` / `...Email` and no `leadTechnicianEmailContact`. Only a full
// work order has the nested contact, so read both — same pair WorkOrderTechAssignedPill reads.
const getLeadTechnician = (event) => {
	const nestedContact = nullSafeGet('lastServiceCall.leadTechnicianEmailContact', event);
	const email = nullSafeGetOrElse(
		'lastServiceCall.leadTechnicianEmail',
		event,
		nullSafeGet('lastServiceCallLeadTechnicianEmail', event)
	);
	const name =
		getContactName(nestedContact || {}).trim() ||
		nullSafeGetOrElse('lastServiceCallLeadTechnicianName', event, '').trim() ||
		email;
	if (!name) {
		return { name: undefined, contact: undefined };
	}
	if (nestedContact) {
		return { name, contact: nestedContact };
	}

	return { name, contact: getFamilyNameAndGivenName({ name }) };
};

interface AppointmentCardProps {
	event: any;
	/** Location columns name the technician on the card; technician columns name the location. */
	isLocationView?: boolean;
	calendarTimeZone?: string;
}

export const AppointmentCard: FC<AppointmentCardProps> = ({ event, isLocationView = false, calendarTimeZone }) => {
	const [popoverVisible, setPopoverVisible] = useState(false);

	if (!event) {
		return null;
	}

	const workOrderId = nullSafeGet('id', event);
	const title = nullSafeGet('title', event);
	const locationName = nullSafeGet('location.name', event);
	const displayEnd = nullSafeGet('displayEnd', event);
	const startMoment = moment(nullSafeGet('start', event));
	const startLabel = startMoment.format(TIME_FORMAT);
	const timeLabel = displayEnd
		? `${startLabel} - ${moment(displayEnd).format(TIME_FORMAT)}`
		: startLabel;

	const { name: technicianName, contact: technicianContact } = getLeadTechnician(event);

	const endMoment = moment(nullSafeGet('end', event));
	const renderedHeightPx =
		startMoment.isValid() && endMoment.isValid()
			? (endMoment.diff(startMoment, 'minutes') / 60) * GRID_HOUR_HEIGHT_PX
			: TWO_ROW_MINIMUM_PX;
	const expandedThreshold =
		isLocationView && !!technicianName ? THREE_ROW_MINIMUM_PX : TWO_ROW_MINIMUM_PX;
	const showsMetaRow = renderedHeightPx >= expandedThreshold;
	const showsTechnicianRow = showsMetaRow && isLocationView && !!technicianName;

	return (
		<Popover
			trigger="click"
			placement="topLeft"
			overlayClassName="workOrderPopoverDisplay_wrapper"
			visible={popoverVisible}
			onVisibleChange={setPopoverVisible}
			content={<ServiceCallPopoverDisplayV2 workOrder={event} displayTimeZone={calendarTimeZone || moment.tz.guess()} />}
		>
			<div
				className={cn(
					'box-border flex h-full min-h-0 flex-col justify-start overflow-hidden px-2 py-0.5',
					popoverVisible && 'appointmentCard--open'
				)}
			>
				<div
					className={`appointmentCard__title footnoteMedium min-h-0 text-grey-9${
						showsMetaRow ? '' : ' appointmentCard__title--singleLine'
					}`}
				>
					{workOrderId ? `#${workOrderId} ` : ''}
					{title}
				</div>
				{showsTechnicianRow ? (
					<div className="footnoteregular flex flex-none flex-row items-center gap-1 overflow-hidden text-character-secondary">
						<ContactAvatar
							hidePopover={true}
							size={TECHNICIAN_AVATAR_SIZE_PX}
							contact={technicianContact}
						/>
						<span className="overflow-hidden text-ellipsis whitespace-nowrap">
							{technicianName}
						</span>
					</div>
				) : null}
				{showsMetaRow ? (
					<div className="footnoteregular mt-1 flex flex-none flex-row items-baseline gap-1 overflow-hidden text-character-secondary">
						{/* Only the technician calendar names the location here. */}
						{!isLocationView && locationName ? (
							<span className="overflow-hidden text-ellipsis whitespace-nowrap">
								{locationName}
							</span>
						) : null}
						{!isLocationView && locationName ? <span className="flex-none">&bull;</span> : null}
						<span className="flex-none whitespace-nowrap">{timeLabel}</span>
					</div>
				) : null}
			</div>
		</Popover>
	);
};
